import { RightOutlined } from '@ant-design/icons';
import { Carousel, Image, Tag } from 'antd';
import Logo from 'components/navigation/Logo';
import { Button } from 'components/ui';
import { colors, screenHeight } from 'design';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import { selectCurrentUser } from 'store/users/selectors';

const useStyles = createUseStyles({
  carousel: {
    backgroundColor: colors.surface.page,
    background: `linear-gradient(0deg, ${colors.surface.page} 0%, white 100%)`,
    backgroundSize: 'auto 120px',
    backgroundPosition: 'top',
    backgroundRepeat: 'no-repeat',
  },
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: screenHeight,
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    padding: '64px 0 4px',
    flex: '0 0 auto',
  },
  // Scrollable area; the group inside is vertically centered via `margin: auto`
  // (which still allows scrolling when the content is taller than the viewport).
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 24px',
  },
  // Title + subtitle + illustration grouped and centered together.
  group: {
    margin: 'auto 0',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  // Clear hierarchy — bold dark title + discrete subtitle.
  // No green inline emphasis (previously confused with clickable text).
  title: {
    fontSize: '1.35rem',
    fontWeight: 'bold',
    lineHeight: 1.25,
    margin: 0,
    color: colors.text.primary,
  },
  subtitle: {
    fontSize: '0.95rem',
    lineHeight: 1.5,
    color: colors.text.tertiary,
    margin: '10px 0 0',
  },
  contentWrapper: {
    width: '100%',
    marginTop: 28,
  },
  footer: {
    flex: '0 0 auto',
    display: 'flex',
    justifyContent: 'center',
    padding: '16px 24px 48px',
  },

  // Specialty pills (page 1)
  specialtyGroupLabel: {
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: colors.text.tertiary,
    margin: '0 0 10px',
    textAlign: 'center',
  },
  specialtyGroup: {
    marginBottom: 20,
  },
  specialtyTagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
  },
  specialtyTag: {
    borderRadius: 20,
    padding: '5px 14px',
    fontSize: '0.9rem',
    backgroundColor: 'white',
    border: `1px solid ${colors.border.subtle}`,
    color: colors.text.primary,
    cursor: 'default',
    margin: 0,
  },

  // Framed illustration slides
  illustrationContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  illustration: {
    width: '78%',
    maxWidth: '320px',
  },

  dots: {
    bottom: '20px',
    '& li > button': {
      backgroundColor: colors.text.tertiary,
    },
    '& li.slick-active > button': {
      backgroundColor: colors.brand.accent,
    },
  },
});

const SPECIALTY_TAGS_MEDICAL = [
  { emoji: '🌸', key: 'gynecologists' },
  { emoji: '🌿', key: 'midwives' },
  { emoji: '🤲', key: 'physiotherapists' },
  { emoji: '🥑', key: 'nutritionists' },
  { emoji: '💬', key: 'psychologists' },
  { emoji: '👶', key: 'childcareNurses' },
  { emoji: '🦴', key: 'osteopaths' },
  { emoji: '👩‍⚕️', key: 'nurses' },
];

const SPECIALTY_TAGS_SOFT = [
  { emoji: '🌱', key: 'naturopaths' },
  { emoji: '🧘', key: 'sophrologists' },
];

export default function OnboardingV2Page() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const styles = useStyles();
  const currentUser = useSelector(selectCurrentUser);

  const carousel = useRef(null);

  const nextPage = (isLastPage: boolean) => {
    if (isLastPage) {
      navigate(routes.home.route);
    } else {
      (carousel?.current as any)?.next();
    }
  };

  const teamPage = (
    <>
      <div className={styles.specialtyGroup}>
        <p className={styles.specialtyGroupLabel}>{t('onboarding.carouselV2.pages.team.groupMedical')}</p>
        <div className={styles.specialtyTagsContainer}>
          {SPECIALTY_TAGS_MEDICAL.map(tag => (
            <Tag key={tag.key} className={styles.specialtyTag}>
              {tag.emoji} {t(`onboarding.carouselV2.pages.team.specialties.${tag.key}`)}
            </Tag>
          ))}
        </div>
      </div>
      <div className={styles.specialtyGroup}>
        <p className={styles.specialtyGroupLabel}>{t('onboarding.carouselV2.pages.team.groupSoft')}</p>
        <div className={styles.specialtyTagsContainer}>
          {SPECIALTY_TAGS_SOFT.map(tag => (
            <Tag key={tag.key} className={styles.specialtyTag}>
              {tag.emoji} {t(`onboarding.carouselV2.pages.team.specialties.${tag.key}`)}
            </Tag>
          ))}
        </div>
      </div>
    </>
  );

  const illustration = (src: string) => (
    <div className={styles.illustrationContainer}>
      <Image alt="" src={src} preview={false} rootClassName={styles.illustration} />
    </div>
  );

  const firstName = currentUser?.first_name;

  const pages: {
    title: string;
    subtitle: string;
    buttonKey?: string;
    content?: React.ReactNode;
  }[] = [
    {
      title: firstName
        ? t('onboarding.carouselV2.pages.welcome.title', { firstName })
        : t('onboarding.carouselV2.pages.welcome.titleNoName'),
      subtitle: t('onboarding.carouselV2.pages.welcome.subtitle'),
      buttonKey: 'onboarding.carouselV2.buttons.discover',
    },
    {
      title: t('onboarding.carouselV2.pages.response.title'),
      subtitle: t('onboarding.carouselV2.pages.response.subtitle'),
      content: illustration(require('../assets/onboarding-chat.png')),
    },
    {
      title: t('onboarding.carouselV2.pages.team.title'),
      subtitle: t('onboarding.carouselV2.pages.team.subtitle'),
      content: teamPage,
    },
    {
      title: t('onboarding.carouselV2.pages.tracking.title'),
      subtitle: t('onboarding.carouselV2.pages.tracking.subtitle'),
      content: illustration(require('../assets/onboarding-tracking.png')),
    },
    {
      title: t('onboarding.carouselV2.pages.content.title'),
      subtitle: t('onboarding.carouselV2.pages.content.subtitle'),
      content: illustration(require('../assets/onboarding-blog-posts.png')),
    },
  ];

  return (
    <Carousel
      ref={carousel}
      className={styles.carousel}
      adaptiveHeight
      infinite={false}
      dots={{ className: styles.dots }}
    >
      {pages.map((page, index) => {
        const isLastPage = index === pages.length - 1;
        return (
          <div key={index}>
            <div className={styles.page}>
              <div className={styles.header}>
                <Logo size="large" />
              </div>
              <div className={styles.body}>
                <div className={styles.group}>
                  <h1 className={styles.title}>{page.title}</h1>
                  <p className={styles.subtitle}>{page.subtitle}</p>
                  {page.content && <div className={styles.contentWrapper}>{page.content}</div>}
                </div>
              </div>
              <div className={styles.footer}>
                <Button
                  variant="primary"
                  onClick={() => nextPage(isLastPage)}
                  icon={<RightOutlined />}
                  iconPosition="end"
                  size="lg"
                >
                  {page.buttonKey
                    ? t(page.buttonKey)
                    : isLastPage
                      ? t('onboarding.carouselV2.buttons.end')
                      : t('onboarding.carouselV2.buttons.next')}
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </Carousel>
  );
}
